// Bridges the "Étapes de raisonnement" (Point 4) tuple to/from a hidden
// fenced block inside the Tahiti `content` markdown, since there is no DB
// column for it yet. The block is stripped out before the body ever reaches
// the editor (see tahiti-adapter.ts's toWord) so it never leaks into the
// visible editor body as literal fence-marker text.
import { emptyNoyauPonderation } from '@/lib/noyaux';
import { reasoningStepsSchema } from '@/schemas/word';
import type { ReasoningSteps } from '@/schemas/word';

const FENCE = 'pms-raisonnement';
const FENCE_RE = new RegExp(`\`\`\`${FENCE}\\n([\\s\\S]*?)\\n\`\`\``);

/** Six all-zero ponderations — the default when a word has no reasoning steps yet. */
export function emptyReasoningSteps(): ReasoningSteps {
  return [
    emptyNoyauPonderation(),
    emptyNoyauPonderation(),
    emptyNoyauPonderation(),
    emptyNoyauPonderation(),
    emptyNoyauPonderation(),
    emptyNoyauPonderation(),
  ];
}

export interface ExtractedReasoningSteps {
  /** `md` with the hidden fenced block removed. */
  content: string;
  steps: ReasoningSteps;
  /** Whether `md` already carried a reasoning block (valid or not) — drives the lazy-persist write rule. */
  hadBlock: boolean;
}

/**
 * Strip the hidden reasoning-steps fenced block from `md`, if present, and
 * parse its 6 noyau ponderations. Falls back to {@link emptyReasoningSteps}
 * when the block is absent or its JSON doesn't validate — never throws.
 */
export function extractReasoningSteps(md: string): ExtractedReasoningSteps {
  const match = FENCE_RE.exec(md);
  if (!match) return { content: md, steps: emptyReasoningSteps(), hadBlock: false };

  const content = (md.slice(0, match.index) + md.slice(match.index + match[0].length))
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  let steps = emptyReasoningSteps();
  try {
    const parsed = reasoningStepsSchema.safeParse(JSON.parse(match[1] ?? '[]'));
    if (parsed.success) steps = parsed.data;
  } catch {
    // Malformed JSON in the block — degrade to empty steps rather than throwing.
  }
  return { content, steps, hadBlock: true };
}

/** Append the hidden reasoning-steps fenced block to `content` (used when persisting). */
export function mergeReasoningSteps(content: string, steps: ReasoningSteps): string {
  const block = `\`\`\`${FENCE}\n${JSON.stringify(steps)}\n\`\`\``;
  return content ? `${content}\n\n${block}` : block;
}
